import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { selectAuthToken } from '@/store/selectors/authSelectors';
import { PaginatedTable, PaginatedTableRowModel } from '@lucky7ventures/lucky-components';
import { getDateFilterOptions, mapDateFilterItems } from '@/utils/dateFilter';
import Spinner from '@/components/UI/Spinner';
import { DateFilter } from '@/components/DateFilter';
import { StyledPaginatedTable } from '@/components/StyledPaginatedTable';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { BffWalletTransactionsResponse, BffWalletTransaction } from '@lucky7ventures/bff-types';
import BffApiService from '@/shared/bffApiService';

// Copied this function from lucky-components lib because the `data` type was set to
// the type which is not active anymore now that wallet transactions was migrated to BFF.
// Amending the type in the lib is also not a option, because we are probably going to ditch
// this lib sometimes in the future.
export const paginatedTableTransactionsMapper = (
  locale: string,
  translations: { transactionType: { deposit: string; withdrawal: string } },
): ((data: BffWalletTransaction[]) => PaginatedTableRowModel[]) => {
  return (transactions: BffWalletTransaction[]): PaginatedTableRowModel[] => {
    if (!transactions) {
      return [];
    }

    return transactions.map(transaction => ({
      id: transaction.transactionId || '',
      cells: [
        {
          text:
            transaction.transactionType === 'Deposit'
              ? translations.transactionType.deposit
              : translations.transactionType.withdrawal,
        },
        {
          text:
            transaction.amount > 0 && transaction.currency
              ? new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: transaction.currency,
                }).format(transaction.amount)
              : '-',
          align: 'center',
        },
        {
          text: transaction.description || '',
          align: 'center',
        },
        {
          text: new Date(transaction.date).toLocaleDateString(locale),
          subtext: `${new Date(transaction.date).toLocaleTimeString(locale)} UTC`,
          align: 'right',
        },
      ],
    }));
  };
};

const PaymentsHistory = (): JSX.Element | null => {
  const locale = useSelector(selectLocale);
  const token = useSelector(selectAuthToken);
  const intl = useIntl();
  const dateFilterOptions = getDateFilterOptions();
  const dateFilterItems = mapDateFilterItems(dateFilterOptions);

  const noDataMsg = intl.formatMessage({
    id: 'history.noTransactions',
  });
  const loadingMsg = <Spinner width={20} height={20} dark />;
  const headerCells = ['history.type', 'history.amount', 'history.method', 'history.date'].map(id =>
    intl.formatMessage({ id }),
  );
  const { loading, data, request } = useBffApiRequest<BffWalletTransactionsResponse>();

  const selectDateRange = (id: number) => {
    const { fromDate, toDate } = dateFilterOptions[id].dateRange;
    request({
      apiMethod: BffApiService.fetchWalletTransactions,
      axiosConfig: { params: { from: fromDate, to: toDate } },
    });
  };

  // This effect will trigger the initial request for the Last 30 days of history
  useEffect(() => {
    const { fromDate, toDate } = dateFilterOptions[1].dateRange;
    request({
      apiMethod: BffApiService.fetchWalletTransactions,
      axiosConfig: { params: { from: fromDate, to: toDate } },
    });
  }, [token]);

  if (!locale) {
    return null;
  }

  return (
    <>
      <DateFilter items={dateFilterItems} selectDateRange={selectDateRange} />
      <StyledPaginatedTable>
        <PaginatedTable<BffWalletTransaction>
          noDataMsg={noDataMsg}
          dataRows={data || []}
          headerCells={headerCells}
          isLoading={loading}
          loadingMsg={loadingMsg}
          rowsMapper={paginatedTableTransactionsMapper(locale, {
            transactionType: {
              deposit: intl.formatMessage({ id: 'history.deposit' }),
              withdrawal: intl.formatMessage({ id: 'history.withdrawal' }),
            },
          })}
        />
      </StyledPaginatedTable>
    </>
  );
};

export default PaymentsHistory;
